<template>
	<page-container class="index" :page="page">
		<heading-image v-if="page.headingImage" type="large">
			<hero-image>
				<div v-if="defaults[locale].homepage.headingImageTitle" class="hero-content row">
					<div class="columns">
						<p class="hero-title">
							{{ defaults[locale].homepage.headingImageTitle }}
						</p>
						<div v-parse-links v-html="defaults[locale].homepage.headingImageContent" />
					</div>
				</div>
				<picture>
					<source
						v-if="page.headingImagePortraitWebp"
						:srcset="page.headingImagePortraitWebp"
						media="(max-width: 500px)"
						type="image/webp"
					/>
					<source
						v-if="page.headingImagePortrait"
						:srcset="page.headingImagePortrait"
						media="(max-width: 500px)"
					/>
					<source :srcset="page.headingImageWebp" type="image/webp" />
					<source :srcset="page.headingImage" />
					<img :src="page.headingImage" :alt="page.headingImageAlt" />
				</picture>
			</hero-image>
		</heading-image>

		<main-content>
			<div class="grid main-intro columns">
				<div class="intro-image">
					<picture>
						<source :srcset="defaults[locale].homepage.introImageWebp" type="image/webp" />
						<source :srcset="defaults[locale].homepage.introImage" />
						<img
							:src="defaults[locale].homepage.introImage"
							:alt="defaults[locale].homepage.introImageAlt"
							loading="lazy"
						/>
					</picture>
				</div>
				<div class="intro-text">
					<h2 class="subtitle">{{ page.subtitle }}</h2>
					<h1>{{ page.title || page.header }}</h1>
					<div v-parse-links v-html="page.content" />
				</div>
			</div>
		</main-content>

		<promoblocks :items="promoblocksData" />

		<section v-if="defaults[locale].homepage.sectionReservationsTitle" class="section-reservations">
			<div class="row">
				<div class="columns column5">
					<h2 class="subtitle">
						{{ defaults[locale].homepage.sectionReservationsSubtitle }}
					</h2>
					<h2>{{ defaults[locale].homepage.sectionReservationsTitle }}</h2>
					<div v-parse-links v-html="defaults[locale].homepage.sectionReservationsContent" />
				</div>
				<div class="columns column7">
					<div class="form-wrapper">
						<nuxt-link :to="localePath(`/contact`)" class="button">
							{{ $t('contactButton') }}
						</nuxt-link>
					</div>
				</div>
			</div>
		</section>

		<section v-if="defaults[locale].homepage.promotionBackgroundImage" class="section-promotion">
			<div class="row">
				<div class="columns column12 align-center">
					<h2 class="subtitle">
						{{ defaults[locale].homepage.promotionsSubtitle }}
					</h2>
					<h2>{{ defaults[locale].homepage.promotionsHeader }}</h2>
					<div v-parse-links v-html="defaults[locale].homepage.promotionsContent" />
				</div>
			</div>
			<picture>
				<source :srcset="defaults[locale].homepage.promotionBackgroundImageWebp" type="image/webp" />
				<source :srcset="defaults[locale].homepage.promotionBackgroundImage" />
				<img
					class="promotions-image"
					:src="defaults[locale].homepage.promotionBackgroundImage"
					loading="lazy"
				/>
			</picture>
		</section>

		<contentblocks :items="page.contentblocks" class="section-contentblocks">
			<template #blocks-header>
				<div class="row blocks-header align-center">
					<div class="columns column12">
						<h2 class="subtitle">
							{{ defaults[locale].homepage.sectionContentblocksSubtitle }}
						</h2>
						<h2>{{ defaults[locale].homepage.sectionContentblocksTitle }}</h2>
					</div>
				</div>
			</template>
		</contentblocks>

		<section v-if="defaults[locale].homepage.contactHeader" class="content-section section-contact contact-info">
			<div class="row">
				<div class="columns column12 align-center">
					<h2 class="subtitle">
						{{ defaults[locale].homepage.contactSubtitle }}
					</h2>
					<h2>
						{{ defaults[locale].homepage.contactHeader }}
					</h2>
					<div v-parse-links v-html="defaults[locale].homepage.contactContent" />
				</div>
			</div>
			<div class="row contactblock">
				<div class="columns column10 contactmap">
					<iframe
						src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2448.7049278015315!2d6.194581199999999!3d52.139688799999995!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47c7946bf077f4d1%3A0xdf6b764ebcf992c3!2sBrasserie%206!5e0!3m2!1snl!2snl!4v1740396578928!5m2!1snl!2snl"
						width="100%"
						height="600"
						frameborder="0"
						style="border: 0"
						allowfullscreen=""
						aria-hidden="false"
						tabindex="0"
					/>
				</div>
				<div class="columns column3 contactinfo">
					<h3>{{ defaults[locale].website.restaurantName }}</h3>

					<span>{{ defaults[locale].website.street }} {{ defaults[locale].website.number }}</span>
					<br />
					<span>{{ defaults[locale].website.postalCode }} {{ defaults[locale].website.city }}</span>
					<br />
					<a :href="`tel:${$phoneRaw(defaults[locale].website.phone)}`">
						<font-awesome-icon class="icon" icon="fal fa-phone" size="1x" />
						{{ defaults[locale].website.phone }}
					</a>
					<br />
					<a :href="`mailto:${defaults[locale].website.email}`">
						<font-awesome-icon class="icon" icon="fal fa-envelope" size="1x" />
						{{ defaults[locale].website.email }}
					</a>
					<br />
					<social-media :socials="socials" />
				</div>
			</div>
		</section>

		<frequently-asked-questions
			v-if="page.frequentlyAskedQuestion"
			:items="page.frequentlyAskedQuestion.faqitems"
			:header="page.frequentlyAskedQuestion.header"
		/>
	</page-container>
</template>

<script setup>
const { page, fetchPage } = usePage();
const { defaults, socials, fetchDefaults } = useDefaults();
const { locale } = useI18n();
const localePath = useLocalePath();

await fetchPage();
await fetchDefaults();

const { data: promoblocksData } = await useWebsiteFetch('promoblocks', {
	query: { language: locale.value },
	key: `${locale.value}/promoblocks`,
});
</script>

<style lang="scss" scoped>
.hero-content {
	position: absolute;
	top: 45%;
	display: flex;
	flex-flow: column nowrap;
	color: #fff;
	text-align: left;
	z-index: 5;
	left: 0;
	right: 0;

	.hero-title {
		display: inline-flex;
		justify-content: center;
		align-items: center;
		max-width: 515px;
		font-family: var(--heading-font-family);
		font-weight: 700;
		color: #fff;
		background-color: rgb(156 186 94 / 90%);
		outline: 2px solid rgb(156 186 94 / 90%);
		font-size: 48px;
		padding: 25px 0 20px 25px;
		line-height: 60px;
		margin-bottom: 20px;
	}

	:deep(> *) {
		max-width: 700px;
	}
}

.main-intro {
	display: grid;
	grid-template-columns: 4fr 5fr 1fr;
	gap: 80px;
	align-items: stretch;

	.intro-image {
		display: flex;
		align-items: stretch;
	}

	.intro-image picture,
	.intro-image img {
		width: 100%;
		height: 100%;
		object-fit: cover;
	}

	.intro-text {
		padding-top: 70px;
	}
}

.section-reservations {
	background-color: var(--tertiary-background-color);
	background-image: url('~/assets/images/reservations-bg.svg');
	background-repeat: no-repeat;
	background-position: bottom;
	position: relative;
	width: 100%;
	padding: 50px 0;
	margin-bottom: -2px;

	h2 {
		color: #fff;
		font-family: var(--heading-font-family);
		line-height: 52px;
	}

	.subtitle {
		color: var(--subtitle-color);
		font-family: var(--body-font-family);
	}

	:deep(p) {
		color: #fff;
	}

	.form-wrapper {
		padding: 40px 30px;
		position: relative;
		height: 100%;

		.button {
			width: auto;
			margin: 10px 0 0;
			padding: 15px 30px;
			position: absolute;
			top: 40%;
			right: 30%;

			&:hover {
				color: var(--cta-color-hover);
				background: #fff;
				outline: 2px solid #fff;
			}
		}

		input,
		select,
		textarea {
			color: #fff;
			width: 100%;
			max-width: 500px;
			padding: 20px;
			border: none;
			box-shadow: 0 0 5px rgb(0 0 0 / 10%);
			font-size: 13px;
			font-family: var(--body-font-family);
		}
	}

	.reservation-form {
		display: flex;
		flex-flow: row wrap;
		gap: 25px;

		> div {
			width: calc(50% - 13px);
		}
	}
}

.section-promotion {
	background: var(--secondary-background-color);
	margin: 0;
	padding: 150px 25px;
	overflow: auto;
	clip-path: inset(0);
	position: relative;

	&::after {
		background: rgb(0 0 0 / 60%);
		position: absolute;
		width: 100%;
		height: 100%;
		left: 0;
		top: 0;
		content: '';
	}

	.row {
		position: relative;
		z-index: 10;
		color: #fff;
		overflow: hidden;
		max-width: 800px;
	}

	.promotions-image {
		position: absolute;
		object-fit: cover;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
	}

	h2:not(.subtitle) {
		color: #fff;
	}

	:deep(.button.no-fill) {
		color: #fff !important;
		outline: 2px solid #fff;
	}

	:deep(.button.no-fill:hover) {
		outline: 2px solid #05462e;
	}
}

.section-contentblocks {
	.blocks-header {
		margin-bottom: 70px;
	}
}

.section-contact {
	background-color: var(--secondary-background-color);

	.row:first-child {
		padding-bottom: 50px;
	}

	.contactblock {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		margin: 40px auto;
		position: relative;

		.contactinfo {
			width: 20% !important;
			position: absolute;
			right: 70px;
			background: #fff;
			display: flex;
			flex-flow: column;
			padding: 50px;
		}
	}
}

@keyframes fade-from-bottom {
	0% {
		opacity: 0;
		transform: translateY(calc(-30% + 100px));
	}

	66% {
		opacity: 0;
	}

	100% {
		opacity: 1;
		transform: translateY(calc(-50% + 100px));
	}
}

@media (max-width: 1480px) {
	.main-intro {
		grid-template-columns: 8fr 10fr;
	}

	.section-contact {
		.contactblock {
			.contactmap {
				width: 85%;
			}

			.contactinfo {
				width: 30% !important;
			}
		}
	}
}

@media (max-width: 1280px) {
	.main-intro {
		grid-template-columns: 8fr 10fr;
	}
}

@media (max-width: 1080px) {
	.hero-content {
		max-width: 650px;
	}

	.main-intro {
		grid-template-columns: 100%;
		gap: 60px;

		.intro-text {
			padding-top: 0;
		}
	}

	.intro-image {
		display: none;
	}

	.section-contact {
		.contactblock {
			display: flex;
			flex-flow: column;
			position: relative;
			padding-bottom: 300px;

			.contactmap {
				width: 100%;
			}

			.contactinfo {
				width: 70% !important;
				bottom: 0;
				left: auto;
				right: auto;
				margin-top: -40px;
			}
		}
	}

	.section-reservations {
		.column5 {
			width: 80% !important;
		}
	}
}

@media (max-width: 680px) {
	.section-promotions {
		padding: 125px 25px;
	}

	.promotions-image {
		position: absolute;
	}

	.hero-content {
		font-size: 15px;
		padding: 0 10px;

		.hero-title {
			font-size: 30px;
			padding: 25px 20px 20px 25px;
			line-height: 40px;
		}
	}

	.section-reservations .reservation-form > div {
		width: 100%;
	}

	.section-reservations {
		.column6 {
			width: 100%;
		}
	}

	.section-reservations .form-wrapper {
		padding: 0 0 50px;

		.button {
			position: relative;
			top: unset;
			right: unset;
		}
	}
}

@media (max-width: 400px) {
	.section-contentblocks {
		.blocks-header {
			margin-bottom: 0;

			h2 {
				font-size: 40px;
			}

			h2.subtitle {
				font-size: 20px;
				margin-bottom: 20px;
			}
		}
	}

	.section-contact {
		.contactblock {
			.contactinfo {
				width: 80% !important;
			}
		}
	}
}
</style>
